/**
 * CommandPalette — HD (Runde 6)
 *
 * Cmd+K / Strg+K-Kommandopalette fuer die globale Suche. Ersetzt das fruehere
 * Inline-Suchfeld in der Topbar; die Topbar traegt jetzt nur noch ein
 * Lupen-Icon, das diese Palette oeffnet.
 *
 * BEWUSSTE ABWEICHUNG VON v1: die Suche ist USER-SCOPED (eigene Hotspots,
 * Wallets, Kontakte, Tags). v1 suchte global ueber alle Hotspots des
 * Netzwerks — das wird in v2 nicht angeboten (Datenschutz, Missbrauchs- und
 * Performance-Risiko). Siehe auch Kommentar in ../layouts/useGlobalSearch.ts.
 *
 * Bedienung:
 * - Cmd+K / Strg+K oeffnet, Escape schliesst
 * - Pfeil hoch/runter navigiert durch die flache Trefferliste (gruppenuebergreifend)
 * - Enter oeffnet den aktiven Treffer
 * - Eingabe wird 250ms debounced (kein Request je Tastendruck)
 *
 * A11y (Runde-6-Review F3):
 * - Tastatur-Handling haengt am Dialog, nicht am Input — Escape/Pfeiltasten
 *   greifen auch, wenn der Fokus das Suchfeld verlassen hat
 * - aria-activedescendant verweist auf den aktiven Treffer, der zusaetzlich per
 *   scrollIntoView({ block: 'nearest' }) sichtbar gehalten wird
 * - beim Schliessen geht der Fokus auf das oeffnende Element zurueck
 * - Statusmeldungen (zu kurz / Suche laeuft / keine Treffer) und die Trefferliste
 *   haengen konsistent am debouncedQuery — nie alte Treffer neben "zu kurz"
 *
 * SICHERHEIT: alle Treffernamen werden als Text gerendert (kein
 * dangerouslySetInnerHTML) — Contact.name/Tag.name sind im Backend nicht
 * HTML-escaped.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Radio, Wallet, Users, Tag as TagIcon, Search, MapPin, StickyNote } from 'lucide-react'
import { useGlobalSearch, normalizeSearchResults } from '../layouts/useGlobalSearch'
import { hotspotDisplayName } from '../lib/hotspotDisplayName'

const DEBOUNCE_MS = 250

type PaletteItem = {
  key: string
  id: number
  label: string
  sublabel?: string
  group: 'hotspots' | 'wallets' | 'contacts' | 'tags' | 'hosts' | 'notes'
}

const GROUP_ORDER: PaletteItem['group'][] = [
  'hotspots',
  'wallets',
  'hosts',
  'contacts',
  'tags',
  'notes',
]

const GROUP_ICON = {
  hotspots: Radio,
  wallets: Wallet,
  contacts: Users,
  tags: TagIcon,
  hosts: MapPin,
  notes: StickyNote,
} as const

const GROUP_LABEL_KEY = {
  hotspots: 'topbar.search.groupHotspots',
  wallets: 'topbar.search.groupWallets',
  contacts: 'topbar.search.groupContacts',
  tags: 'topbar.search.groupTags',
  hosts: 'topbar.search.groupHosts',
  notes: 'topbar.search.groupNotes',
} as const

export type CommandPaletteProps = {
  open: boolean
  onClose: () => void
}

/**
 * Aeussere Huelle: rendert die Palette nur im geoeffneten Zustand. Dadurch
 * startet jeder Oeffnen-Vorgang mit frischem State (leeres Suchfeld, Auswahl
 * auf dem ersten Treffer) — ohne Reset-Effekt.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  if (!open) return null
  return <CommandPaletteDialog onClose={onClose} />
}

function CommandPaletteDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('shell')
  const navigate = useNavigate()
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const {
    data: searchResults,
    error: searchError,
    refetch: refetchSearch,
  } = useGlobalSearch(debouncedQuery)

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  // R6-Review F3: Fokus zurueck auf das Element, das die Palette geoeffnet hat
  // (Lupen-Trigger bzw. das zuletzt fokussierte Element bei Cmd+K). Der Wert
  // wird im ERSTEN Render gelesen — ein Effekt liefe erst nach dem autoFocus
  // des Suchfeldes und wuerde dieses selbst zurueckgeben.
  const [previouslyFocused] = useState<HTMLElement | null>(() =>
    typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null),
  )
  useEffect(() => {
    return () => {
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus?.()
      }
    }
  }, [previouslyFocused])

  // R6-Review F3: der ANGEZEIGTE Zustand haengt am debouncedQuery, nicht am
  // rohen Eingabewert — sonst stehen alte Treffer neben dem "zu kurz"-Hinweis.
  const settled = debouncedQuery === query
  const showQueryTooShort = query.length > 0 && query.length < 2
  const showError = settled && !showQueryTooShort && debouncedQuery.length >= 2 && !!searchError
  const pending =
    !showQueryTooShort && query.length >= 2 && !showError && (!settled || !searchResults)
  /** Ergebnisse nur zeigen, wenn sie zur aktuellen Eingabe gehoeren. */
  const showResults = settled && !showQueryTooShort && debouncedQuery.length >= 2 && !!searchResults

  const items = useMemo<PaletteItem[]>(() => {
    if (!showResults || !searchResults) return []
    // normalizeSearchResults haelt die Palette robust, falls ein aelteres
    // Backend nur hotspots/wallets liefert.
    const grouped = normalizeSearchResults(searchResults)
    const list: PaletteItem[] = []
    for (const h of grouped.hotspots) {
      list.push({
        key: `hotspots-${h.id}`,
        id: h.id,
        label: hotspotDisplayName(h.name, h.address, t('topbar.search.unknownHotspot')),
        sublabel: h.address,
        group: 'hotspots',
      })
    }
    for (const w of grouped.wallets) {
      list.push({
        key: `wallets-${w.id}`,
        id: w.id,
        label: w.name || w.address,
        sublabel: w.address,
        group: 'wallets',
      })
    }
    for (const host of grouped.hosts) {
      list.push({
        key: `hosts-${host.id}`,
        id: host.id,
        label: host.name,
        sublabel: host.email ?? undefined,
        group: 'hosts',
      })
    }
    for (const c of grouped.contacts) {
      list.push({
        key: `contacts-${c.id}`,
        id: c.id,
        label: c.name,
        sublabel: c.email ?? undefined,
        group: 'contacts',
      })
    }
    for (const tag of grouped.tags) {
      list.push({ key: `tags-${tag.id}`, id: tag.id, label: tag.name, group: 'tags' })
    }
    for (const note of grouped.notes) {
      list.push({
        key: `notes-${note.id}`,
        id: note.id,
        label: note.title || note.text,
        sublabel: note.title ? note.text : undefined,
        group: 'notes',
      })
    }
    return list
  }, [searchResults, showResults, t])

  // Auswahl waehrend des Renderns clampen: schrumpft die Trefferliste (neue
  // Suchergebnisse), faellt die Auswahl auf den ersten Treffer zurueck — ohne
  // Reset-Effekt und ohne Zusatzrender.
  const safeIndex = activeIndex < items.length ? activeIndex : 0

  const noResults =
    !pending && !showError && !showQueryTooShort && query.length >= 2 && items.length === 0

  const activeItem = items[safeIndex]
  const activeOptionId = activeItem ? `cmdk-option-${activeItem.key}` : undefined

  // R6-Review F3: den aktiven Treffer in den sichtbaren Bereich scrollen.
  const resultsRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!activeOptionId) return
    const el = resultsRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    // jsdom kennt scrollIntoView nicht — optional aufrufen.
    el?.scrollIntoView?.({ block: 'nearest' })
  }, [activeOptionId])

  const openItem = useCallback(
    (item: PaletteItem) => {
      onClose()
      const id = String(item.id)
      switch (item.group) {
        case 'hotspots':
          void navigate({ to: '/hotspots/$id', params: { id } })
          break
        case 'wallets':
          void navigate({ to: '/wallets/$id', params: { id } })
          break
        case 'contacts':
          void navigate({ to: '/contacts/$id', params: { id } })
          break
        case 'tags':
          void navigate({ to: '/tags/$id', params: { id } })
          break
        case 'hosts':
          void navigate({ to: '/hosts/$id', params: { id } })
          break
        case 'notes':
          void navigate({ to: '/notes' })
          break
      }
    },
    [navigate, onClose],
  )

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setQuery(val)
    setActiveIndex(0)
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(() => setDebouncedQuery(val), DEBOUNCE_MS)
  }, [])

  // R6-Review F3: Das Tastatur-Handling haengt am DIALOG, nicht am Input —
  // so funktionieren Escape/Pfeiltasten auch, wenn der Fokus (z.B. per Tab oder
  // Mausklick auf einen Treffer) das Suchfeld verlassen hat.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (items.length > 0) setActiveIndex((i) => ((i < items.length ? i : 0) + 1) % items.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (items.length > 0)
          setActiveIndex((i) => ((i < items.length ? i : 0) - 1 + items.length) % items.length)
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        const item = items[safeIndex]
        if (item) openItem(item)
      }
    },
    [items, safeIndex, openItem, onClose],
  )

  let flatIndex = -1

  return (
    <div
      className="cmdk-overlay"
      data-testid="command-palette-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="cmdk-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('topbar.search.ariaLabel')}
        onKeyDown={handleKeyDown}
        data-testid="command-palette"
      >
        <div className="cmdk-input-row">
          <Search size={16} aria-hidden="true" className="cmdk-input-icon" />
          <input
            /* Autofokus ist hier gewollt: der Dialog wird ausschliesslich auf
               explizite Nutzeraktion (Lupe/Cmd+K) geoeffnet und das Suchfeld
               ist sein einziger Einstiegspunkt. */
            autoFocus
            className="cmdk-input"
            type="text"
            value={query}
            onChange={handleChange}
            placeholder={t('topbar.search.placeholder')}
            aria-label={t('topbar.search.ariaLabel')}
            aria-controls="cmdk-results"
            aria-activedescendant={activeOptionId}
            aria-expanded
            aria-autocomplete="list"
            role="combobox"
            autoComplete="off"
            data-testid="global-search-input"
          />
          <kbd className="cmdk-kbd" aria-hidden="true">
            esc
          </kbd>
        </div>

        <div
          id="cmdk-results"
          ref={resultsRef}
          role="listbox"
          aria-label={t('topbar.search.resultsAriaLabel')}
          data-testid="search-results-dropdown"
          className="cmdk-results"
        >
          {showQueryTooShort && (
            <div className="cmdk-empty" data-testid="search-too-short">
              {t('topbar.search.minChars')}
            </div>
          )}
          {pending && (
            <div className="cmdk-empty" data-testid="search-pending">
              {t('topbar.search.pending')}
            </div>
          )}
          {noResults && (
            <div className="cmdk-empty" data-testid="search-no-results">
              {t('topbar.search.noResults')}
            </div>
          )}
          {showError && (
            <div className="cmdk-empty" data-testid="search-error">
              <span>{t('topbar.search.error')}</span>
              <button type="button" onClick={() => void refetchSearch()} data-testid="search-retry">
                {t('topbar.search.retry')}
              </button>
            </div>
          )}

          {GROUP_ORDER.map((group) => {
            const groupItems = items.filter((i) => i.group === group)
            if (groupItems.length === 0) return null
            const Icon = GROUP_ICON[group]
            const groupLabelId = `cmdk-group-${group}`
            return (
              <div key={group} role="group" aria-labelledby={groupLabelId}>
                <div className="cmdk-group-label" id={groupLabelId}>
                  {t(GROUP_LABEL_KEY[group])}
                </div>
                {groupItems.map((item) => {
                  flatIndex += 1
                  const isActive = flatIndex === safeIndex
                  const singular = group.slice(0, -1)
                  return (
                    <button
                      key={item.key}
                      id={`cmdk-option-${item.key}`}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      data-active={isActive ? 'true' : undefined}
                      className={`cmdk-item${isActive ? ' is-active' : ''}`}
                      data-testid={`search-result-${singular}-${item.id}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => openItem(item)}
                    >
                      <Icon size={14} aria-hidden="true" className="cmdk-item-icon" />
                      <span className="cmdk-item-label">{item.label}</span>
                      {item.sublabel && item.sublabel !== item.label && (
                        <span className="cmdk-item-sub">{item.sublabel}</span>
                      )}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>

        <div className="cmdk-footer" aria-hidden="true">
          {t('topbar.search.hint')}
        </div>
      </div>
    </div>
  )
}

export default CommandPalette
